import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Paper } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { Search } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { SCUIBreadCrumb, SCUICustomDataGrid } from '../../atomic/molecules';
import { SCUIContent } from '../../atomic/organisms';
import { SCUIButton, SCUILoadingLinear, SCUINoRecordComponent } from '../../atomic/atoms';
import { PageUrl } from '../../types/enums/pageUrl-enums';
import airdropShipment from '../../assets/icons/svg/pageIcons/airdropShipment.svg';
import OmnichannelQuery from '../../services/query/omnichannel/Omnichannel.services';
import PostCheckoutQuery from '../../services/query/postCheckout/PostCheckoutQuery.service';
import StoreCargoBlockingCommand from '../../services/command/storeCargoBlocking/StoreCargoBlockingCommand.services';
import StoreCargoBlockingQuery from '../../services/query/storeCargoBlocking/StoreCargoBlockingQuery.services';
import TimeHelper from '../../utils/time';
import { downloadExcel } from '../../utils/excelFileHelper';
import { SearchDrawer } from './components';
import { SCB, scbCls } from './storeCargoBlocking.classes';

const DEFAULT_PAGE_SIZE = 10;
const EXCEL_FOLDER_NAME = 'StoreCargoBlocking';

interface Option {
    id: number;
    name: string;
}

interface StoreOption {
    code: string;
    name: string;
}

interface GridRow {
    id: string;
    storeCode: string;
    storeName: string;
    shippingCompanyId: number;
    shippingCompanyName: string;
    changedOn: string;
    changedBy: string;
}

const extractStoreOptions = (res: any): StoreOption[] => {
    const stores = res?.data || res?.stores || [];
    if (!Array.isArray(stores)) {
        return [];
    }

    return stores
        .filter((item: any) => item?.storeCode && item?.storeName)
        .map((item: any) => ({
            code: item.storeCode,
            name: item.storeName,
        }))
        .sort((a: StoreOption, b: StoreOption) => a.name.localeCompare(b.name));
};

const getApiErrorMessage = (response: any, fallback: string) => response?.message || fallback;

export const StoreCargoBlocking = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [isSearched, setIsSearched] = useState(false);
    const [searchDrawerOpen, setSearchDrawerOpen] = useState(false);
    const [addDrawerOpen, setAddDrawerOpen] = useState(false);
    const [gridData, setGridData] = useState<GridRow[]>([]);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [pageNumber, setPageNumber] = useState(0);
    const [cargoCompanyOptions, setCargoCompanyOptions] = useState<Option[]>([]);
    const [storeOptions, setStoreOptions] = useState<StoreOption[]>([]);
    const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);

    const { t } = useTranslation();

    useEffect(() => {
        PostCheckoutQuery.GetShippingCompanies()
            .then((res: any) => {
                if (res?.shippingCompanies && Array.isArray(res.shippingCompanies)) {
                    const options = res.shippingCompanies
                        .filter(
                            (company: any) =>
                                company?.shippingCompanyId != null &&
                                company?.name != null &&
                                company?.isCargoCompany === true
                        )
                        .map((company: any) => ({
                            id: company.shippingCompanyId,
                            name: company.name ?? '',
                        }));
                    setCargoCompanyOptions(options);
                }
            })
            .catch((err: any) => {
                console.error('Error fetching cargo companies:', err);
            });

        OmnichannelQuery.StoreList()
            .then((res: any) => {
                setStoreOptions(extractStoreOptions(res));
            })
            .catch((err: any) => {
                console.error('Error fetching stores:', err);
            });
    }, []);

    const handleSearch = useCallback(
        (filterParams: any) => {
            setIsLoading(true);
            setSearchDrawerOpen(false);

            const payload = {
                shippingCompanyId: filterParams.cargoCompanyId,
                storeCodes: filterParams.storeCodes,
            };

            StoreCargoBlockingQuery.GetBlockedStores(payload)
                .then((res: any) => {
                    const response = res?.data || res || {};

                    if (response?.isSuccess === false) {
                        Swal.fire({
                            icon: 'error',
                            title: t('general.error'),
                            text: getApiErrorMessage(response, t('storeCargoBlocking.searchError')),
                            confirmButtonText: t('general.ok'),
                        });
                        return;
                    }

                    const items = response?.blockedStores || [];
                    const transformedData: GridRow[] = Array.isArray(items)
                        ? items.map((item: any, index: number) => ({
                              id: `${item.storeCode || index}_${item.shippingCompanyId || index}`,
                              storeCode: item.storeCode || '-',
                              storeName: item.storeName || '-',
                              shippingCompanyId: item.shippingCompanyId || 0,
                              shippingCompanyName: item.shippingCompanyName || '-',
                              changedOn: item.changedOn || item.createdOn || '',
                              changedBy: item.changedBy || '-',
                          }))
                        : [];

                    setGridData(transformedData);
                    setSelectedRowIds([]);
                    setIsSearched(true);
                })
                .catch((err: any) => {
                    console.error('Error fetching blocked stores:', err);
                    Swal.fire({
                        icon: 'error',
                        title: t('general.error'),
                        text: t('storeCargoBlocking.searchError'),
                        confirmButtonText: t('general.ok'),
                    });
                })
                .finally(() => {
                    setIsLoading(false);
                });
        },
        [t]
    );

    const handleDelete = useCallback(
        async (row: GridRow) => {
            const result = await Swal.fire({
                title: t('general.sureText'),
                text: t('storeCargoBlocking.deleteConfirm'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: t('general.delete'),
                cancelButtonText: t('general.cancel'),
            });

            if (!result.isConfirmed) {
                return;
            }

            const previousData = [...gridData];
            setGridData((prev) => prev.filter((item) => item.id !== row.id));

            const payload = {
                shippingCompanyId: row.shippingCompanyId,
                storeCodes: [row.storeCode],
            };

            StoreCargoBlockingCommand.DeleteBlockedStore(payload)
                .then((res: any) => {
                    const response = res?.data || res || {};

                    if (response?.isSuccess === false) {
                        setGridData(previousData);
                        Swal.fire({
                            icon: 'error',
                            title: t('general.error'),
                            text: getApiErrorMessage(response, t('storeCargoBlocking.deleteError')),
                            confirmButtonText: t('general.ok'),
                        });
                        return;
                    }

                    Swal.fire({
                        icon: 'success',
                        title: t('general.success'),
                        text: response?.message || t('storeCargoBlocking.deleteSuccess'),
                        confirmButtonText: t('general.ok'),
                    });
                })
                .catch((err: any) => {
                    console.error('Error deleting blocked store:', err);
                    setGridData(previousData);
                    Swal.fire({
                        icon: 'error',
                        title: t('general.error'),
                        text: t('storeCargoBlocking.deleteError'),
                        confirmButtonText: t('general.ok'),
                    });
                });
        },
        [t, gridData]
    );

    const handleAddNew = useCallback(() => {
        setAddDrawerOpen(true);
    }, []);

    const handleAddSuccess = useCallback(
        (filterParams: any) => {
            if (filterParams) {
                handleSearch(filterParams);
            }
        },
        [handleSearch]
    );

    const handleSelectionModelChange = useCallback((selectionModel: any) => {
        const normalizedSelection = Array.isArray(selectionModel) ? selectionModel.map(String) : [];
        setSelectedRowIds(normalizedSelection);
    }, []);

    const handleBulkDelete = useCallback(async () => {
        if (selectedRowIds.length === 0) {
            return;
        }

        const result = await Swal.fire({
            title: t('general.sureText'),
            text: t('storeCargoBlocking.bulkDeleteConfirm', { count: selectedRowIds.length }),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('general.delete'),
            cancelButtonText: t('general.cancel'),
        });

        if (!result.isConfirmed) {
            return;
        }

        const previousData = [...gridData];
        const idsToDelete = [...selectedRowIds];
        const selectedRows = gridData.filter((item) => idsToDelete.includes(item.id));
        const deleteGroups = selectedRows.reduce(
            (acc, row) => {
                const key = row.shippingCompanyId;
                if (!acc[key]) {
                    acc[key] = {
                        shippingCompanyId: row.shippingCompanyId,
                        storeCodes: [],
                    };
                }
                acc[key].storeCodes.push(row.storeCode);
                return acc;
            },
            {} as Record<number, { shippingCompanyId: number; storeCodes: string[] }>
        );

        const bulkPayloads = Object.values(deleteGroups).map((group) => ({
            shippingCompanyId: group.shippingCompanyId,
            storeCodes: Array.from(new Set(group.storeCodes)),
        }));

        setGridData((prev) => prev.filter((item) => !idsToDelete.includes(item.id)));
        setSelectedRowIds([]);

        Promise.all(bulkPayloads.map((payload) => StoreCargoBlockingCommand.DeleteBlockedStore(payload)))
            .then((responses: any[]) => {
                const failedResponse = responses.find((res) => {
                    const response = res?.data || res || {};
                    return response?.isSuccess === false;
                });

                if (failedResponse) {
                    const response = failedResponse?.data || failedResponse || {};
                    setGridData(previousData);
                    Swal.fire({
                        icon: 'error',
                        title: t('general.error'),
                        text: getApiErrorMessage(response, t('storeCargoBlocking.deleteError')),
                        confirmButtonText: t('general.ok'),
                    });
                    return;
                }

                Swal.fire({
                    icon: 'success',
                    title: t('general.success'),
                    text: t('storeCargoBlocking.deleteSuccess'),
                    timer: 1500,
                });
            })
            .catch(() => {
                setGridData(previousData);
                Swal.fire({
                    icon: 'error',
                    title: t('general.error'),
                    text: t('general.errorOccured'),
                    confirmButtonText: t('general.ok'),
                });
            });
    }, [selectedRowIds, t, gridData]);

    const handleExportExcel = useCallback(() => {
        const excelRows = gridData.map((row) => ({
            storeCode: row.storeCode || '-',
            name: row.storeName || '-',
            cargoCompanyName: row.shippingCompanyName || '-',
            updatedDate: row.changedOn ? TimeHelper.formatDate(row.changedOn) : '-',
            updatedBy: row.changedBy || '-',
        }));

        const now = new Date();
        const dd = String(now.getDate()).padStart(2, '0');
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const yyyy = now.getFullYear();
        const fileName = `MagazaBazliKargoYasaklama_${dd}.${mm}.${yyyy}.xlsx`;

        downloadExcel({
            excelDownloadData: excelRows,
            excelDownloadFolderName: EXCEL_FOLDER_NAME,
            fileNameOverride: fileName,
        });
    }, [gridData]);

    const openSearchDrawer = useCallback(() => {
        setSearchDrawerOpen(true);
    }, []);

    const closeSearchDrawer = useCallback(() => {
        setSearchDrawerOpen(false);
    }, []);

    const closeAddDrawer = useCallback(() => {
        setAddDrawerOpen(false);
    }, []);

    const resetSearch = useCallback(() => {
        setIsSearched(false);
        setGridData([]);
        setSelectedRowIds([]);
    }, []);

    const deleteButtonSx = useMemo(
        () => ({
            minWidth: '30px !important',
            width: '30px !important',
            minHeight: '30px !important',
            height: '30px !important',
            backgroundColor: isLoading ? '#ccc' : 'red',
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: 'none',
            p: 0,
            m: 0,
            '&:hover': {
                backgroundColor: isLoading ? '#ccc' : '#c00',
                boxShadow: 'none',
            },
        }),
        [isLoading]
    );

    const columnData = useMemo(
        () => [
            {
                field: 'storeCode',
                headerName: t('storeCargoBlocking.columns.storeCode'),
                flex: 1,
                minWidth: 120,
                renderCell: (params: any) => <span>{params?.row?.storeCode ?? '-'}</span>,
            },
            {
                field: 'storeName',
                headerName: t('storeCargoBlocking.columns.storeName'),
                flex: 1,
                minWidth: 150,
                renderCell: (params: any) => <span>{params?.row?.storeName ?? '-'}</span>,
            },
            {
                field: 'shippingCompanyName',
                headerName: t('storeCargoBlocking.columns.cargoCompanyName'),
                flex: 1,
                minWidth: 150,
                renderCell: (params: any) => <span>{params?.row?.shippingCompanyName ?? '-'}</span>,
            },
            {
                field: 'changedOn',
                headerName: t('storeCargoBlocking.columns.updatedDate'),
                width: 160,
                align: 'center',
                headerAlign: 'center',
                renderCell: (params: any) => {
                    const dateValue = params?.row?.changedOn;
                    if (dateValue && dateValue !== '-' && dateValue !== '') {
                        return <span>{TimeHelper.formatDate(dateValue)}</span>;
                    }
                    return <span>-</span>;
                },
            },
            {
                field: 'changedBy',
                headerName: t('storeCargoBlocking.columns.updatedBy'),
                flex: 1,
                minWidth: 150,
                renderCell: (params: any) => <span>{params?.row?.changedBy ?? '-'}</span>,
            },
            {
                field: 'actions',
                headerName: t('storeCargoBlocking.columns.delete'),
                width: 100,
                align: 'center',
                headerAlign: 'center',
                sortable: false,
                renderCell: (params: any) => (
                    <Button
                        onClick={() => handleDelete(params.row)}
                        disabled={isLoading}
                        sx={deleteButtonSx}
                        className={scbCls(SCB.input, SCB.inputButton, SCB.btnDeleteRow)}
                    >
                        <DeleteIcon sx={{ color: 'white', fontSize: 14 }} />
                    </Button>
                ),
            },
        ],
        [t, isLoading, handleDelete, deleteButtonSx]
    );

    return (
        <SCUIContent className={SCB.page}>
            <Paper
                component="div"
                className={SCB.paper}
                sx={{
                    p: '24px',
                    paddingTop: '36px',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                }}
            >
                <SCUIBreadCrumb
                    title={t('storeCargoBlocking.title')}
                    componentLink={PageUrl.storeCargoBlocking.url}
                    onClick={resetSearch}
                />
                <SCUILoadingLinear display={isLoading} />

                <SearchDrawer
                    isDrawerOpen={searchDrawerOpen}
                    handleDrawerClose={closeSearchDrawer}
                    handleSearch={handleSearch}
                    mode="search"
                    cargoCompanyOptions={cargoCompanyOptions}
                    storeOptions={storeOptions}
                    isLoading={isLoading}
                />

                <SearchDrawer
                    isDrawerOpen={addDrawerOpen}
                    handleDrawerClose={closeAddDrawer}
                    onAddSuccess={handleAddSuccess}
                    mode="add"
                    cargoCompanyOptions={cargoCompanyOptions}
                    storeOptions={storeOptions}
                    setIsLoading={setIsLoading}
                    isLoading={isLoading}
                />

                {isSearched ? (
                    <Box sx={{ marginTop: '24px' }}>
                        <Box
                            className={SCB.toolbar}
                            sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '12px' }}
                        >
                            <SCUIButton
                                text={t('general.addNew')}
                                type="primary"
                                sizeType="fixed"
                                style={{ height: '35px' }}
                                size={'m'}
                                buttonType="button"
                                onClick={handleAddNew}
                                showStartIcon
                                startIcon={<AddIcon sx={{ color: '#fff' }} />}
                                disabled={isLoading}
                                className={scbCls(SCB.input, SCB.inputButton, SCB.btnAddNew)}
                            />
                            <SCUIButton
                                text={t('general.exportToExcel')}
                                type="secondary"
                                sizeType="fixed"
                                style={{ height: '35px' }}
                                size={'m'}
                                buttonType="button"
                                onClick={handleExportExcel}
                                showStartIcon
                                startIcon={<FileDownloadIcon sx={{ color: '#0d6efd' }} />}
                                disabled={isLoading}
                                className={scbCls(SCB.input, SCB.inputButton, SCB.btnExportExcel)}
                            />
                            <SCUIButton
                                text={t('general.search')}
                                type="secondary"
                                sizeType="fixed"
                                style={{ height: '35px' }}
                                size={'m'}
                                buttonType="button"
                                onClick={openSearchDrawer}
                                showStartIcon
                                startIcon={<Search sx={{ color: '#0d6efd' }} />}
                                disabled={isLoading}
                                className={scbCls(SCB.input, SCB.inputButton, SCB.btnOpenSearch)}
                            />
                        </Box>
                        <Box
                            className={SCB.bulkActions}
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: '8px',
                                marginBottom: '4px',
                                marginTop: '24px',
                            }}
                        >
                            {selectedRowIds?.length > 0 && (
                                <Button
                                    variant="contained"
                                    color="error"
                                    onClick={handleBulkDelete}
                                    startIcon={<DeleteIcon />}
                                    disabled={isLoading}
                                    sx={{ padding: '8px 16px !important' }}
                                    className={scbCls(SCB.input, SCB.inputButton, SCB.btnBulkDelete)}
                                >
                                    {`${t('storeCargoBlocking.bulkDelete')} (${selectedRowIds?.length})`}
                                </Button>
                            )}
                        </Box>
                        <Box className={SCB.datagrid}>
                            <SCUICustomDataGrid
                                checkboxSelection
                                selectionOnClick={true}
                                onSelectionModelChange={handleSelectionModelChange}
                                selectionModel={selectedRowIds}
                                onPageSizeChange={setPageSize}
                                pageSize={pageSize}
                                onPageChange={setPageNumber}
                                getRowId={(row: any) => row?.id}
                                page={pageNumber}
                                rows={gridData}
                                columns={columnData}
                                slotProps={{
                                    baseCheckbox: {
                                        className: scbCls(SCB.input, SCB.inputCheckbox, SCB.cbGridSelection),
                                    },
                                }}
                            />
                        </Box>
                    </Box>
                ) : (
                    <Box
                        className={SCB.emptyState}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <SCUINoRecordComponent img={airdropShipment} text={t('storeCargoBlocking.search')} />
                        <SCUIButton
                            text={t('general.search')}
                            type="primary"
                            sizeType="fixed"
                            size={'m'}
                            showStartIcon
                            style={{ width: '120px', marginTop: '16px' }}
                            disabled={isLoading}
                            startIcon={
                                isLoading ? (
                                    <CircularProgress size={20} style={{ color: 'white' }} />
                                ) : (
                                    <Search style={{ color: 'white' }} />
                                )
                            }
                            onClick={openSearchDrawer}
                            className={scbCls(SCB.input, SCB.inputButton, SCB.btnEmptySearch)}
                        />
                    </Box>
                )}
            </Paper>
        </SCUIContent>
    );
};
